import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCaptcha } from '@/plugins/captcha';
import { useSocialStore } from '@/stores/social';

const routes = [
    {
        path: '/',
        name: 'main',
        component: () => import('../views/MainView.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/LoginView.vue'),
        meta: { requiresAuth: false },
        props: { successMessage: '' },
    },
    {
        path: '/logout',
        name: 'logout',
        meta: { requiresAuth: true },
    },
    {
        path: '/reminder',
        name: 'reminder',
        component: () => import('../views/ReminderView.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/reset/:confirmationToken',
        name: 'reset',
        component: () => import('../views/ResetView.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/password-change',
        name: 'password-change',
        component: () => import('../views/PasswordChangeView.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/email-change',
        name: 'email-change',
        component: () => import('../views/EmailChangeView.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/email-confirm',
        name: 'email-confirm',
        component: () => import('../views/EmailConfirmView.vue'),
    },
    {
        path: '/social/:provider/confirmation',
        name: 'social-confirm',
        meta: { requiresAuth: true },
    },
    {
        path: '/social',
        name: 'social',
        component: () => import('../views/SocialConnectView.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/:pathMatch(.*)*',
        name: '404',
        component: () => import('../views/Error404View.vue'),
    },
] as RouteRecordRaw[];

const router = createRouter({
    history: createWebHistory('/'),
    routes,
});

router.beforeEach(async (to) => {
    const { load } = useCaptcha()
    const store = useAuthStore()

    await load()

    if (store.isAuthorized === undefined || (store.isAuthorized && store.user === null)) {
        await store.me()
    }

    if (to.name === 'logout') {
        if (store.isAuthorized) {
            await store.logout()
        }

        return { name: 'login', query: to.query };
    }

    if (to.name === 'social-confirm') {
        const provider = to.params.provider as string
        const { code, state } = getSocialParams()
        const { confirmSocialLogin, confirmSocial } = useSocialStore()

        if (!code || !state) {
            return {
                name: store.isAuthorized ? 'social' : 'login',
                query: to.query,
            }
        }

        if (store.isAuthorized) {
            await confirmSocial(provider, code, state)

            return { name: 'social' }
        }

        if (await confirmSocialLogin(provider, code, state)) {
            await store.me()
            return { name: 'main' }
        }

        delete to.query.code
        delete to.query.state
        delete to.query.cid
    }

    if (!store.isAuthorized && to.query['fast-login']) {
        const fastLoginToken = to.query['fast-login'].toString()
        await store.fastLogin(fastLoginToken)
    }

    const route = { query: to.query };
    if (to.meta.requiresAuth === true && !store.isAuthorized) {
        return { name: 'login', ...route };
    } else if (to.meta.requiresAuth === false && store.isAuthorized) {
        return { name: 'main', ...route };
    }
})

const getSocialParams = () => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')

    return { code, state }
}

export default router;
